import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * 바이랭귀지(동시 표시) 기능을 제공하는 커스텀 훅
 * 
 * @returns {Object} { tb, bilingualMode, secondaryLanguage, toggleBilingual, setSecondaryLanguage }
 */
export const useBilingual = () => {
  const { t, i18n } = useTranslation();
  const [bilingualMode, setBilingualMode] = useState(false);
  const [secondaryLanguage, setSecondaryLanguage] = useState('en');

  // 로컬 스토리지에서 설정 로드
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setBilingualMode(user.bilingual_mode === 1);
        setSecondaryLanguage(user.secondary_language || 'en');
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }
  }, []);

  /**
   * 바이랭귀지 텍스트 반환
   * @param {string} key - i18n 키 (예: 'dashboard.title')
   * @param {string} options - 추가 옵션 (예: 변수 치환)
   * @returns {string} - "주 언어 (부 언어)" 형태 또는 주 언어만
   */
  const tb = (key, options = {}) => {
    const currentLanguage = i18n.language || 'ko';
    const primaryText = t(key, options);

    // 바이랭귀지 모드가 OFF이면 주 언어만 반환
    if (!bilingualMode) {
      return primaryText;
    }

    // 주 언어와 부 언어가 같으면 중복 표시 방지
    if (currentLanguage === secondaryLanguage) {
      return primaryText;
    }

    // i18n.changeLanguage() 를 렌더 중 직접 호출하면 무한 리렌더가 발생하므로
    // 리소스 번들에서 직접 번역 텍스트를 가져옴
    const resources = i18n.getResourceBundle(secondaryLanguage, 'translation');
    if (!resources) {
      return primaryText;
    }

    const secondaryText = key.split('.').reduce((obj, k) => obj?.[k], resources);
    if (!secondaryText || secondaryText === primaryText) {
      return primaryText;
    }

    // "주 언어 (부 언어)" 형태로 반환
    return `${primaryText} (${secondaryText})`;
  };

  /**
   * 바이랭귀지 모드 토글
   */
  const toggleBilingual = async () => {
    const newMode = !bilingualMode;
    setBilingualMode(newMode);

    // localStorage 및 서버에 저장
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        user.bilingual_mode = newMode ? 1 : 0;
        localStorage.setItem('user', JSON.stringify(user));

        // 서버에 업데이트 요청
        const token = localStorage.getItem('token');
        if (token && user.id) {
          await fetch(`${API_BASE_URL}/api/students/${user.id}/bilingual-settings`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              bilingual_mode: newMode ? 1 : 0,
              secondary_language: secondaryLanguage
            })
          });
        }
      } catch (error) {
        console.error('Failed to update bilingual settings:', error);
      }
    }
  };

  /**
   * 부 언어 변경
   */
  const updateSecondaryLanguage = async (lang) => {
    setSecondaryLanguage(lang);

    // localStorage 및 서버에 저장
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        user.secondary_language = lang;
        localStorage.setItem('user', JSON.stringify(user));

        // 서버에 업데이트 요청
        const token = localStorage.getItem('token');
        if (token && user.id) {
          await fetch(`${API_BASE_URL}/api/students/${user.id}/bilingual-settings`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              bilingual_mode: bilingualMode ? 1 : 0,
              secondary_language: lang
            })
          });
        }
      } catch (error) {
        console.error('Failed to update secondary language:', error);
      }
    }
  };

  return {
    tb,                          // Translate Bilingual 함수
    bilingualMode,               // 바이랭귀지 모드 상태 (true/false)
    secondaryLanguage,           // 부 언어 코드
    toggleBilingual,             // 바이랭귀지 ON/OFF 토글
    updateSecondaryLanguage      // 부 언어 변경 함수
  };
};
